// Frontend-only credits storage for demo purposes.
// Backed by localStorage; structured for a future backend swap.
//
// Schema (v1):
// {
//   version: 1,
//   total: number,
//   blocks: {
//     [refKey]: {
//       refKey: string,
//       count: number,
//       status: 'blocked' | 'consumed',
//       createdAt: string,
//       consumedAt?: string,
//       metadata?: object
//     }
//   }
// }

const STORAGE_PREFIX = 'SJAI_CREDITS_V1:';

export const CREDITS_EVENTS = {
  changed: 'credits:changed',
};

export function isCreditsDebugEnabled() {
  try {
    return String(localStorage.getItem('creditsDebug') || '') === '1';
  } catch {
    return false;
  }
}

function nowIso() {
  return new Date().toISOString();
}

function safeJsonParse(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function resolveCreditsScopeKey(explicitCompanyId) {
  // Prefer an explicit companyId; otherwise try common keys used in the app.
  const cid =
    explicitCompanyId ||
    localStorage.getItem('companyAccountId') ||
    localStorage.getItem('companyId') ||
    localStorage.getItem('companyID') ||
    sessionStorage.getItem('companyAccountId') ||
    sessionStorage.getItem('companyId') ||
    sessionStorage.getItem('companyID') ||
    'demo';

  return `${STORAGE_PREFIX}${String(cid)}`;
}

export function notifyCreditsChanged() {
  // storage events don't fire on the same tab, so we emit a custom event too.
  try {
    window.dispatchEvent(new Event(CREDITS_EVENTS.changed));
  } catch {
    // no-op
  }
}

export function readCreditsStore(scopeKey) {
  const raw = localStorage.getItem(scopeKey);
  const parsed = safeJsonParse(raw);

  if (!parsed || typeof parsed !== 'object') return null;
  if (parsed.version !== 1) return null;
  if (typeof parsed.total !== 'number') return null;
  if (!parsed.blocks || typeof parsed.blocks !== 'object') return null;

  return parsed;
}

export function writeCreditsStore(scopeKey, store) {
  localStorage.setItem(scopeKey, JSON.stringify(store));
  notifyCreditsChanged();
}

export function seedCreditsIfEmpty(scopeKey, { total = 30 } = {}) {
  const existing = readCreditsStore(scopeKey);
  if (existing) return existing;

  const seeded = {
    version: 1,
    total: Number(total) || 0,
    blocks: {},
  };

  writeCreditsStore(scopeKey, seeded);
  return seeded;
}

export function resetCredits(scopeKey, { total = 20 } = {}) {
  const reset = {
    version: 1,
    total: Number(total) || 0,
    blocks: {},
  };
  writeCreditsStore(scopeKey, reset);
  return reset;
}

export function updateCreditsStore(scopeKey, updater) {
  const current = seedCreditsIfEmpty(scopeKey);
  const next = updater(current);
  writeCreditsStore(scopeKey, next);
  return next;
}

export function computeCreditsSummary(store) {
  const total = Number(store?.total || 0);
  const blocks = store?.blocks && typeof store.blocks === 'object' ? store.blocks : {};

  let blocked = 0;
  let used = 0;

  for (const key of Object.keys(blocks)) {
    const entry = blocks[key];
    const count = Number(entry?.count || 0);
    if (!count) continue;
    if (entry.status === 'blocked') blocked += count;
    if (entry.status === 'consumed') used += count;
  }

  const available = Math.max(0, total - blocked - used);

  return {
    total,
    available,
    blocked,
    used,
    blocks,
  };
}

export function makeCreditsRefKey({ companyId, jobId, candidateIdOrEmail, testType }) {
  return [
    String(companyId ?? ''),
    String(jobId ?? ''),
    String(candidateIdOrEmail ?? ''),
    String(testType ?? ''),
  ].join(':');
}

